import { useState, type ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '../../design-system/data-display';

export type ReviewQueueLayoutProps = {
  leftPanel: ReactNode;
  centerPanel: ReactNode;
  rightPanel: ReactNode;
  mobileWorkPanel?: ReactNode;
  mobileRubricPanel?: ReactNode;
  mobileFeedbackPanel?: ReactNode;
};

const panelDesktop =
  'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:rounded-dg-card lg:border lg:border-dg-border lg:bg-dg-bg-surface/95 lg:shadow-dg-card dark:lg:border-slate-700/70 dark:lg:bg-slate-900/45 dark:lg:shadow-dg-soft-dark';

type MobilePanel = 'work' | 'rubric' | 'feedback';

/**
 * Submission view 3-panel shell: queue (~22%) · preview (~54%) · grading (~28%).
 * Below `lg`, segmented tabs: Work | Rubric | Feedback.
 */
export function ReviewQueueLayout({
  leftPanel,
  centerPanel,
  rightPanel,
  mobileWorkPanel,
  mobileRubricPanel,
  mobileFeedbackPanel,
}: ReviewQueueLayoutProps): ReactElement {
  const { t } = useTranslation();
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('work');

  const segments = [
    { id: 'work' as const, label: t('audit.studio.mobileTabWork') },
    { id: 'rubric' as const, label: t('audit.studio.mobileTabRubric') },
    { id: 'feedback' as const, label: t('audit.studio.mobileTabFeedback') },
  ];

  const work = mobileWorkPanel ?? centerPanel;
  const rubric = mobileRubricPanel ?? rightPanel;
  const feedback = mobileFeedbackPanel ?? rightPanel;

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden lg:hidden">
        <SegmentedControl segments={segments} value={mobilePanel} onChange={setMobilePanel} />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {mobilePanel === 'work' ? (
            <main className={panelDesktop} aria-label={t('audit.studio.detailAria')}>
              {work}
            </main>
          ) : null}
          {mobilePanel === 'rubric' ? (
            <aside className={panelDesktop} aria-label={t('audit.gradeStudio.rubricTitle')}>
              {rubric}
            </aside>
          ) : null}
          {mobilePanel === 'feedback' ? (
            <aside className={panelDesktop} aria-label={t('audit.gradeStudio.feedbackTitle')}>
              {feedback}
            </aside>
          ) : null}
        </div>
      </div>
      <div
        className="hidden min-h-0 flex-1 gap-0 overflow-hidden lg:grid lg:grid-cols-[minmax(220px,260px)_minmax(0,1.15fr)_minmax(240px,30%)] xl:grid-cols-[minmax(240px,260px)_minmax(0,1.2fr)_minmax(280px,30%)]"
        data-dg-grade-studio
        role="presentation"
      >
        <aside className={panelDesktop} aria-label={t('audit.studio.queueAria')}>
          {leftPanel}
        </aside>
        <main
          className={`${panelDesktop} flex-1 lg:border-l-0 lg:border-r-0 lg:rounded-none dark:lg:border-slate-700/70`}
          aria-label={t('audit.studio.detailAria')}
        >
          {centerPanel}
        </main>
        <aside className={panelDesktop} aria-label={t('audit.studio.actionsAria')}>
          {rightPanel}
        </aside>
      </div>
    </>
  );
}
